import { create } from "zustand";
import { persist } from "zustand/middleware";

// Default sidebar width
const DEFAULT_SIDEBAR_WIDTH = 260;
const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 400;

// App favorites layout item (simplified GridLayout.Layout)
export interface AppLayoutItem {
  i: string; // app id
  x: number;
  y: number;
  w: number;
  h: number;
}

// ============================================================================
// Store Interface
// ============================================================================

interface SidebarUIState {
  // UI State (persisted locally)
  isOpen: boolean;
  isLocked: boolean;
  sidebarWidth: number;
  currentWorkspaceIndex: number;

  // App favorites edit mode (iOS-style reordering)
  isAppEditMode: boolean;
  appFavoritesLayout: AppLayoutItem[]; // Full layout with positions

  // Actions
  setIsOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setIsLocked: (locked: boolean) => void;
  toggleLock: () => void;
  setSidebarWidth: (width: number) => void;
  setCurrentWorkspaceIndex: (index: number) => void;

  // App favorites edit mode actions
  setAppEditMode: (isEditing: boolean) => void;
  setAppFavoritesLayout: (layout: AppLayoutItem[]) => void;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useSidebarStore = create<SidebarUIState>()(
  persist(
    (set) => ({
      // Initial state
      isOpen: false,
      isLocked: false,
      sidebarWidth: DEFAULT_SIDEBAR_WIDTH,
      currentWorkspaceIndex: 0,

      // App favorites edit mode state
      isAppEditMode: false,
      appFavoritesLayout: [], // Empty means use default layout

      // Actions
      setIsOpen: (open) => set({ isOpen: open }),
      toggleSidebar: () => set((state) => ({ isOpen: !state.isOpen })),
      setIsLocked: (locked) =>
        set({
          isLocked: locked,
          isOpen: locked ? true : false,
        }),
      toggleLock: () =>
        set((state) => {
          const newLocked = !state.isLocked;
          return {
            isLocked: newLocked,
            isOpen: newLocked,
          };
        }),
      setSidebarWidth: (width) =>
        set({
          sidebarWidth: Math.max(
            MIN_SIDEBAR_WIDTH,
            Math.min(MAX_SIDEBAR_WIDTH, width)
          ),
        }),
      setCurrentWorkspaceIndex: (index) =>
        set({ currentWorkspaceIndex: index }),

      // App favorites edit mode actions
      setAppEditMode: (isEditing) => set({ isAppEditMode: isEditing }),
      setAppFavoritesLayout: (layout) => set({ appFavoritesLayout: layout }),
    }),
    {
      name: "sidebar-ui-storage",
      partialize: (state) => ({
        isLocked: state.isLocked,
        sidebarWidth: state.sidebarWidth,
        currentWorkspaceIndex: state.currentWorkspaceIndex,
        appFavoritesLayout: state.appFavoritesLayout,
      }),
      version: 7, // Bump version - removed dataChatMode/dataChatWidth (now using split view)
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;

        // Clean up old dashboard state fields from version 1
        if (version < 2) {
          delete state.localDashboards;
          delete state.unsavedDashboardIds;
          delete state.splitView;
        }

        // Clean up activeDashboardId and openDashboardIds from version 2
        // These are now derived from URL
        if (version < 3) {
          delete state.activeDashboardId;
          delete state.openDashboardIds;
        }

        // Migrate appFavoritesOrder to appFavoritesLayout (version 4 -> 6)
        if (version < 4) {
          state.appFavoritesLayout = [];
        }
        // Convert old order array to layout array
        if (version < 6) {
          delete state.appFavoritesOrder;
          if (!state.appFavoritesLayout) {
            state.appFavoritesLayout = [];
          }
        }

        // Remove multiPanel from version 5 (now using View store)
        if (version < 5) {
          delete state.multiPanel;
        }

        // Remove dataChatMode and dataChatWidth from version 7 (now using split view)
        if (version < 7) {
          delete state.dataChatMode;
          delete state.dataChatWidth;
        }

        return state;
      },
    }
  )
);

// Export constants
export {
  DEFAULT_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
  MAX_SIDEBAR_WIDTH,
};
